<template>
  <div class="w-full px-4 sm:px-6 lg:px-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Reports</h1>
      </div>
    </div>
    <div class="mt-8 flex flex-col">
      <div
        class="mb-4 flex w-full flex-wrap items-center justify-between gap-2"
      >
        <div class="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
          💡 Tip: Hover over column headers for detailed explanations
        </div>
        <BaseDateIntervalSelect
          v-model="datePickerModel"
          disable-unbounded
          disable-future-dates
        />
      </div>

      <div class="flex flex-col space-y-12">
        <div>
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">Overview</h2>
              <p class="text-background-fg/70 text-xs">
                Key metrics and performance indicators for arrivals, ANPR
                detection, and overall site activity.
              </p>
            </div>
            <button
              @click="onExportOverview"
              class="border-primary text-primary hover:bg-primary col-span-4 rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white sm:col-span-1 sm:col-start-3"
            >
              Export Overview Report
            </button>
          </div>

          <BaseTable
            :is-loading="isOverviewReportLoading"
            key-field="siteName"
            :rows="overviewReportData || []"
            :columns="overviewReportColumns"
            :footers="overviewTotals ? [overviewTotals] : undefined"
            wrap-headers
            class="mb-4"
          />
        </div>

        <div>
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">
                Arrival Times
              </h2>
              <p class="text-background-fg/70 text-xs">
                Heatmap visualization showing arrival patterns and peak times
                throughout the day.
              </p>
            </div>
            <button
              @click="onExportArrivalTimes"
              class="border-primary text-primary hover:bg-primary col-span-4 rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white sm:col-span-1 sm:col-start-3"
            >
              Export Arrival Times Report
            </button>
          </div>
          <ChartHeatmap :rows="heatmapRows" class="mb-4" show-percentage />
        </div>

        <div v-if="isDigitalArrivals">
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">ANPR</h2>
              <p class="text-background-fg/70 text-xs">
                Automatic Number Plate Recognition statistics including
                detection rates and barrier operations.
              </p>
            </div>
            <button
              @click="onExportAnpr"
              class="border-primary text-primary hover:bg-primary col-span-4 rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white sm:col-span-1 sm:col-start-3"
            >
              Export ANPR Report
            </button>
          </div>
          <BaseTable
            :is-loading="isAnprReportLoading"
            key-field="siteName"
            :rows="anprReportData || []"
            :columns="anprReportColumns"
            :footers="anprTotals ? [anprTotals] : undefined"
            wrap-headers
          />
        </div>

        <div>
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">SMS</h2>
              <p class="text-background-fg/70 text-xs">
                SMS messaging statistics including sent/received messages,
                response rates, and cost analysis.
              </p>
            </div>
            <div class="flex gap-2">
              <button
                @click="onExportSmsReport"
                class="border-primary text-primary hover:bg-primary col-span-4 rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white sm:col-span-1 sm:col-start-3"
              >
                Export SMS Report
              </button>
              <button
                @click="onExportSmsData"
                class="border-primary text-primary hover:bg-primary col-span-4 rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white sm:col-span-1 sm:col-start-3"
              >
                Export SMS Data
              </button>
            </div>
          </div>
          <BaseTable
            :is-loading="isSmsDataLoading"
            key-field="siteName"
            :rows="smsData || []"
            :columns="smsReportColumns"
            :footers="smsTotals ? [smsTotals] : undefined"
            wrap-headers
          />
        </div>

        <div v-if="showHeatmap">
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">
                Pitch Usage
              </h2>
              <p class="text-background-fg/70 text-xs">
                Heatmap showing pitch occupancy intensity across the site.
              </p>
            </div>
          </div>
          <ChartPitchHeatmap
            v-if="siteMapImage && heatmapReportData?.length"
            :site-map-image="siteMapImage"
            :pitch-data="heatmapReportData"
          />
          <div
            v-else-if="isHeatmapLoading"
            class="flex items-center justify-center rounded-lg border bg-gray-50 p-12"
          >
            <span class="text-sm text-gray-500">Loading heatmap...</span>
          </div>
        </div>

        <div v-if="isCompanion">
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">
                Companion Funnel
              </h2>
              <p class="text-background-fg/70 text-xs">
                Where guests drop out between the Companion link and check-in.
              </p>
            </div>
          </div>

          <BaseTable
            :is-loading="isCompanionFunnelLoading"
            key-field="siteName"
            :rows="companionFunnelData || []"
            :columns="companionFunnelColumns"
            wrap-headers
            class="mb-4"
          />
        </div>

        <div>
          <div
            class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
          >
            <div class="pb-2.5">
              <h2 class="mb-1 text-xl font-medium leading-tight">
                Import Problems
              </h2>
              <p class="text-background-fg/70 text-xs">
                Bookings with a missing or unusable registration or phone
                number.
              </p>
            </div>
            <button
              @click="onExportDataQuality"
              class="border-primary text-primary hover:bg-primary col-span-4 rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white sm:col-span-1 sm:col-start-3"
            >
              Export Import Problems
            </button>
          </div>

          <BaseTable
            :is-loading="isDataQualityLoading"
            key-field="siteName"
            :rows="dataQualityData || []"
            :columns="dataQualityColumns"
            wrap-headers
            class="mb-4"
          />
        </div>

        <StorageReports v-if="hasStorageCompound" :filters="filters" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  exportDataQualityReport,
  type DataQualityReportRow,
} from "@/services/reports/getDataQualityReport";
import { type CompanionFunnelReportRow } from "@/services/reports/getCompanionFunnelReport";
import { type AnprReportRow } from "@/services/reports/getAnprReport";
import { type OverviewReportRow } from "@/services/reports/getOverviewReport";
import { type SmsDataRow, useSmsTotals } from "@/services/reports/getSmsData";
import { type BaseReportFilters } from "@/services/reports/reports";
import { type ColumnDefinition } from "@/types/table";

const { id: currentSiteId } = useCurrentSite();
const isDigitalArrivals = useIsDigitalArrivals();
// One site at a time: false for "All sites".
const hasStorageCompound = useHasStorageCompound();

const { datePickerModel } = useDatePickerContext();

const filters = reactiveComputed<BaseReportFilters>(() => ({
  siteId: currentSiteId.value ?? undefined,
  ...{
    fromDt: datePickerModel.value.start,
    untilDt: datePickerModel.value.end,
  },
}));

// Pitch usage heatmap
const siteMapImage = useSiteMapImage();
const showHeatmap = computed(
  () => currentSiteId.value != null && !!siteMapImage.value,
);
const { data: heatmapReportData, isFetching: isHeatmapLoading } =
  useHeatmapReport(filters);

const allOverviewReportColumns: ColumnDefinition<OverviewReportRow>[] = [
  {
    label: "Site",
    name: "site",
    getValue: (row) => row.siteName,
  },
  {
    label: "Total Arrivals",
    name: "totalArrivals",
    tooltip: "Number of bookings that arrived",
    getValue: (row) => {
      return `${row.totalArrivals}`;
    },
    align: "left",
  },
  {
    label: "ANPR Arrivals",
    name: "anprArrivals",
    tooltip: "Number of bookings that were automatically arrived by ANPR",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalAnprArrivals}`;
      }
      return `${row.totalAnprArrivals}<span class="text-background-fg/50"> (${row.percentageAnprArrivals})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Reception Arrivals",
    name: "receptionArrivals",
    tooltip: "Number of bookings that were manually arrived by reception",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalReceptionArrivals}`;
      }
      return `${row.totalReceptionArrivals}<span class="text-background-fg/50"> (${row.percentageReceptionArrivals})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Outside Arrival",
    name: "outsideEntryTimeAttempts",
    tooltip:
      "Number of arrivals that attempted to enter before their scheduled arrival time",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalOutsideEntryTimeAttempts}`;
      }
      return `${row.totalOutsideEntryTimeAttempts}<span class="text-background-fg/50"> (${row.percentageOutsideEntryTimeAttempts})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "SMS Pitch",
    name: "smsPitchDetails",
    tooltip: "Number of arrivals that checked in via SMS",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalSmsPitchDetails}`;
      }
      return `${row.totalSmsPitchDetails}<span class="text-background-fg/50"> (${row.percentageSmsPitchDetails})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Reception Pitch",
    name: "receptionPitchDetails",
    tooltip: "Number of arrivals that checked in via reception",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalReceptionPitchDetails}`;
      }
      return `${row.totalReceptionPitchDetails}<span class="text-background-fg/50"> (${row.percentageReceptionPitchDetails})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Fast Check-in",
    name: "fastCheckIn",
    tooltip:
      "Number of bookings that were automatically arrived by ANPR and checked in via SMS",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalFastCheckIn}`;
      }
      return `${row.totalFastCheckIn}<span class="text-background-fg/50"> (${row.percentageFastCheckIn})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Paid in Advance",
    name: "paidInAdvance",
    tooltip: "Number of arrivals with no outstanding balance upon arrival",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalPaidInAdvance}`;
      }
      return `${row.totalPaidInAdvance}<span class="text-background-fg/50"> (${row.percentagePaidInAdvance})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Balance Due",
    name: "balanceDue",
    tooltip: "Number of arrivals with an outstanding balance upon arrival",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalBalanceDue}`;
      }
      return `${row.totalBalanceDue}<span class="text-background-fg/50"> (${row.percentageBalanceDue})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "ABF",
    name: "abf",
    tooltip:
      "Touring arrivals with ABF, shown as a % of touring arrivals (excludes EF/seasonal/storage/ferry)",
    getValue: (row) => {
      if (row.totalArrivals === 0) {
        return `${row.totalAbf}`;
      }
      return `${row.totalAbf}<span class="text-background-fg/50"> (${row.percentageAbf})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Companion Used",
    name: "companionUsed",
    tooltip:
      "Guests who used the Arrival Companion — to check in, to confirm their registration, or both. As a % of those who could have used it: a companion site with SMS on, a touring pitch that isn't a tent, and a phone number we can text.",
    getValue: (row) => {
      if (!row.siteIsCompanion) {
        return `<span class="text-background-fg/50">—</span>`;
      }
      if (row.totalArrivals === 0) {
        return `${row.totalCompanionUsed}`;
      }
      return `${row.totalCompanionUsed}<span class="text-background-fg/50"> (${row.percentageCompanionUsed})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
];

// ANPR-derived columns only make sense for digital-arrivals sites.
const overviewReportColumns = computed(() =>
  allOverviewReportColumns.filter(
    (column) =>
      isDigitalArrivals.value ||
      !["anprArrivals", "fastCheckIn"].includes(column.name),
  ),
);

const { data: overviewReportData, isFetching: isOverviewReportLoading } =
  useOverviewReport(filters);

const overviewTotals = computed(() => {
  if (!overviewReportData.value) return null;

  const totals = overviewReportData.value.reduce(
    (acc, row) => ({
      totalArrivals: acc.totalArrivals + row.totalArrivals,
      totalTouringArrivals: acc.totalTouringArrivals + row.totalTouringArrivals,
      totalTouringExperienceFreedomArrivals:
        acc.totalTouringExperienceFreedomArrivals +
        row.totalTouringExperienceFreedomArrivals,
      totalAnprArrivals: acc.totalAnprArrivals + row.totalAnprArrivals,
      totalReceptionArrivals:
        acc.totalReceptionArrivals + row.totalReceptionArrivals,
      totalOutsideEntryTimeAttempts:
        acc.totalOutsideEntryTimeAttempts + row.totalOutsideEntryTimeAttempts,
      totalSmsPitchDetails: acc.totalSmsPitchDetails + row.totalSmsPitchDetails,
      totalReceptionPitchDetails:
        acc.totalReceptionPitchDetails + row.totalReceptionPitchDetails,
      totalFastCheckIn: acc.totalFastCheckIn + row.totalFastCheckIn,
      totalPaidInAdvance: acc.totalPaidInAdvance + row.totalPaidInAdvance,
      totalBalanceDue: acc.totalBalanceDue + row.totalBalanceDue,
      totalAbf: acc.totalAbf + row.totalAbf,
      totalCompanionUsed:
        acc.totalCompanionUsed +
        (row.siteIsCompanion ? row.totalCompanionUsed : 0),
      totalCompanionArrivals:
        acc.totalCompanionArrivals +
        (row.siteIsCompanion ? row.totalArrivals : 0),
      totalCompanionEligible:
        acc.totalCompanionEligible +
        (row.siteIsCompanion ? row.totalCompanionEligible : 0),
    }),
    {
      totalArrivals: 0,
      totalTouringArrivals: 0,
      totalTouringExperienceFreedomArrivals: 0,
      totalAnprArrivals: 0,
      totalReceptionArrivals: 0,
      totalOutsideEntryTimeAttempts: 0,
      totalSmsPitchDetails: 0,
      totalReceptionPitchDetails: 0,
      totalFastCheckIn: 0,
      totalPaidInAdvance: 0,
      totalBalanceDue: 0,
      totalAbf: 0,
      totalCompanionUsed: 0,
      totalCompanionArrivals: 0,
      totalCompanionEligible: 0,
    },
  );

  return {
    ...totals,
    siteName: "Total",
    percentageAnprArrivals: totals.totalArrivals
      ? `${Math.round((totals.totalAnprArrivals / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageReceptionArrivals: totals.totalArrivals
      ? `${Math.round((totals.totalReceptionArrivals / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageOutsideEntryTimeAttempts: totals.totalArrivals
      ? `${Math.round((totals.totalOutsideEntryTimeAttempts / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageSmsPitchDetails: totals.totalArrivals
      ? `${Math.round((totals.totalSmsPitchDetails / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageReceptionPitchDetails: totals.totalArrivals
      ? `${Math.round((totals.totalReceptionPitchDetails / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageFastCheckIn: totals.totalArrivals
      ? `${Math.round((totals.totalFastCheckIn / totals.totalArrivals) * 100)}%`
      : "0%",
    percentagePaidInAdvance: totals.totalArrivals
      ? `${Math.round((totals.totalPaidInAdvance / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageBalanceDue: totals.totalArrivals
      ? `${Math.round((totals.totalBalanceDue / totals.totalArrivals) * 100)}%`
      : "0%",
    percentageAbf: totals.totalTouringArrivals
      ? `${Math.round((totals.totalAbf / totals.totalTouringArrivals) * 100)}%`
      : "0%",
    siteIsCompanion: totals.totalCompanionArrivals > 0,
    percentageCompanionUsed: totals.totalCompanionEligible
      ? `${Math.round((totals.totalCompanionUsed / totals.totalCompanionEligible) * 100)}%`
      : "0%",
  };
});

const anprReportColumns: ColumnDefinition<AnprReportRow>[] = [
  {
    label: "Site",
    name: "site",
    getValue: (row) => row.siteName,
  },
  {
    label: "ANPR Reads",
    name: "anprReads",
    tooltip: "Total number of ANPR camera detections",
    getValue: (row) => row.totalAnprReads,
    align: "left",
  },
  {
    label: "Barrier Opens",
    name: "barrierOpens",
    tooltip: "Successful barrier openings (percentage of ANPR reads)",
    getValue: (row) => {
      if (row.totalAnprReads === 0) {
        return `${row.totalBarrierOpens}`;
      }
      const percentage = Math.round(
        (row.totalBarrierOpens / row.totalAnprReads) * 100,
      );
      return `${row.totalBarrierOpens}<span class="text-background-fg/50"> (${percentage}%)</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Denied",
    name: "denied",
    tooltip: "Access denied (percentage of ANPR reads)",
    getValue: (row) => {
      if (row.totalAnprReads === 0) {
        return `${row.totalDenied}`;
      }
      const percentage = Math.round(
        (row.totalDenied / row.totalAnprReads) * 100,
      );
      return `${row.totalDenied}<span class="text-background-fg/50"> (${percentage}%)</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Fuzzy Matches",
    name: "fuzzyMatched",
    getValue: (row) =>
      `${row.totalFuzzyMatched}<span class="text-background-fg/50"> (${row.percentageFuzzyMatched})</span>`,
    align: "left",
    renderHtml: true,
  },
];
const { data: anprReportData, isFetching: isAnprReportLoading } =
  useAnprReport(filters);

const anprTotals = computed(() => {
  if (!anprReportData.value) return null;

  const totals = anprReportData.value.reduce(
    (acc, row) => ({
      siteName: "Total",
      totalAnprReads: acc.totalAnprReads + row.totalAnprReads,
      totalBarrierOpens: acc.totalBarrierOpens + row.totalBarrierOpens,
      totalFuzzyMatched: acc.totalFuzzyMatched + row.totalFuzzyMatched,
      totalDenied: acc.totalDenied + row.totalDenied,
    }),
    {
      siteName: "Total",
      totalAnprReads: 0,
      totalBarrierOpens: 0,
      totalFuzzyMatched: 0,
      totalDenied: 0,
    },
  );

  // Calculate percentages based on totals
  return {
    ...totals,
    percentageFuzzyMatched: totals.totalAnprReads
      ? `${Math.round((totals.totalFuzzyMatched / totals.totalAnprReads) * 100)}%`
      : "0%",
    percentageBarrierOpens: totals.totalAnprReads
      ? `${Math.round((totals.totalBarrierOpens / totals.totalAnprReads) * 100)}%`
      : "0%",
    percentageDenied: totals.totalAnprReads
      ? `${Math.round((totals.totalDenied / totals.totalAnprReads) * 100)}%`
      : "0%",
  };
});

const smsReportColumns: ColumnDefinition<SmsDataRow>[] = [
  {
    label: "Site",
    name: "site",
    getValue: (row) => row.siteName,
  },
  {
    label: "Sent",
    name: "sent",
    tooltip: "Total SMS messages sent",
    getValue: (row) => row.totalSent,
    align: "left",
  },
  {
    label: "Local",
    name: "local",
    tooltip: "SMS sent to local numbers",
    getValue: (row) => row.totalLocal,
    align: "left",
  },
  {
    label: "International",
    name: "international",
    tooltip: "SMS sent to international numbers",
    getValue: (row) => row.totalInternational,
    align: "left",
  },
  {
    label: "Local Credits",
    name: "localCredits",
    tooltip: "SMS credits consumed for local messages",
    getValue: (row) => row.totalLocalCreditsUsed,
    align: "left",
  },
  {
    label: "International Credits",
    name: "internationalCredits",
    tooltip: "SMS credits consumed for international messages",
    getValue: (row) => row.totalInternationalCreditsUsed,
    align: "left",
  },
  {
    label: "Cost",
    name: "cost",
    tooltip: "Total cost based on credits used",
    getValue: (row) => `£${row.totalCost.toFixed(2)}`,
    align: "left",
  },
  {
    label: "Received",
    name: "incoming",
    tooltip: "Total SMS responses received",
    getValue: (row) => row.totalIncoming,
    align: "left",
  },

  {
    label: "Pitch Response",
    name: "pitchResponse",
    tooltip: "Responses to pitch requests (percentage of pitch requests sent)",
    getValue: (row) =>
      `${row.totalPitchResponse}<span class="text-background-fg/50">/${row.totalSentPitch} (${row.percentagePitchResponse})</span>`,
    align: "left",
    renderHtml: true,
  },
  {
    label: "Registration Response",
    name: "regResponse",
    tooltip:
      "Responses to registration checks (percentage of registration requests sent)",
    getValue: (row) =>
      `${row.totalRegResponse}<span class="text-background-fg/50">/${row.totalSentReg} (${row.percentageRegResponse})</span>`,
    align: "left",
    renderHtml: true,
  },
  {
    label: "Language Response",
    name: "languageResponse",
    tooltip:
      "Responses to language preference (percentage of language requests sent)",
    getValue: (row) => {
      return `${row.totalLanguageResponse}<span class="text-background-fg/50">/${row.totalSentLanguage} (${row.percentageLanguageResponse})</span>`;
    },
    align: "left",
    renderHtml: true,
  },
  {
    label: "Alerts",
    name: "smsAlerts",
    tooltip: "SMS-related system alerts generated",
    getValue: (row) => row.totalSmsAlert,
    align: "left",
  },
];
const { data: smsData, isFetching: isSmsDataLoading } = useSmsData(filters);
const { data: smsTotalsData } = useSmsTotals(filters);

const smsTotals = computed(() => {
  if (!smsTotalsData.value) return null;
  return { siteName: "Total", ...smsTotalsData.value } as SmsDataRow;
});

const onExportOverview = () => {
  exportOverviewReport(filters);
};

const onExportArrivalTimes = () => {
  exportArrivalTimeReport(filters);
};

const onExportAnpr = () => {
  exportAnprReport(filters);
};

const onExportSmsData = () => {
  exportSmsData(filters);
};

const onExportSmsReport = () => {
  exportSmsReport(filters);
};

const { data: arrivalTimeReportData } = useArrivalTimeReport(filters);

const heatmapRows = computed(() =>
  Object.fromEntries(
    Object.entries(
      groupBy(arrivalTimeReportData.value || [], (row) => row.siteName),
    ).map(([siteName, rows]) => [
      siteName,
      cleanHeatmapRow(
        rows.map(({ numberOfArrivals, startTime }) => ({
          x: startTime,
          y: numberOfArrivals,
        })),
      ),
    ]),
  ),
);

const cleanHeatmapRow = (row: { x: string; y: number }[]) => {
  return Object.entries(
    row
      .sort((a, b) => a.x.localeCompare(b.x))
      .reduce(
        (accumulator, { x, y }) => {
          const [h] = x.split(":");
          const ampm = Number(h) > 12 ? "pm" : "am";
          const hourNumber = Number(h) % 12 || 12;
          if (Number(h) < 7) {
            return {
              ...accumulator,
              ["< 7am"]: accumulator["< 7am"] ? accumulator["< 7am"] + y : y,
            };
          } else if (Number(h) >= 21) {
            return {
              ...accumulator,
              ["> 9pm"]: accumulator["< 9pm"] ? accumulator["< 9pm"] + y : y,
            };
          } else {
            return {
              ...accumulator,
              [`${hourNumber}${ampm}`]: y,
            };
          }
        },
        {} as Record<string, number>,
      ),
  ).map(([x, y]) => ({ x, y }));
};

// Companion funnel + fast check-in problems
const isCompanion = useIsCompanion();

const { data: companionFunnelData, isFetching: isCompanionFunnelLoading } =
  useCompanionFunnelReport(filters);
const { data: dataQualityData, isFetching: isDataQualityLoading } =
  useDataQualityReport(filters);

const onExportDataQuality = () => {
  exportDataQualityReport(filters);
};

const withPercentage = (value: number, percentage: string) =>
  `${value}<span class="text-background-fg/50"> (${percentage})</span>`;

const companionFunnelColumns: ColumnDefinition<CompanionFunnelReportRow>[] = [
  { label: "Site", name: "siteName", getValue: (row) => row.siteName },
  {
    label: "Eligible",
    name: "eligible",
    tooltip:
      "Guests who arrived in the period and could have used the Companion: a companion site with SMS on, a touring pitch that isn't a tent, and a phone number we can actually text.",
    getValue: (row) => `${row.totalEligible}`,
  },
  {
    label: "Link sent",
    name: "sent",
    tooltip:
      "Bookings a Companion link reached. Usually a message we sent against the booking; also counts a member who requested their own link from the portal, which arrives filed against no booking. As a % of eligible.",
    renderHtml: true,
    getValue: (row) => withPercentage(row.totalSent, row.percentageSent),
  },
  {
    label: "Link opened",
    name: "clicked",
    tooltip:
      "Bookings whose link was opened. As a % of those sent one. Blank for messages sent before click tracking existed, so older ranges under-report.",
    renderHtml: true,
    getValue: (row) => withPercentage(row.totalClicked, row.percentageClicked),
  },
  {
    label: "Signed in",
    name: "loggedIn",
    tooltip:
      "Bookings the member actually got into the portal for. Counted per booking, so a member who signs in once and then works through several bookings counts for each of them. As a % of opened.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalLoggedIn, row.percentageLoggedIn),
  },
  {
    label: "Reg confirmed",
    name: "registered",
    tooltip:
      "Confirmed their vehicle registration in the portal, usually days before arrival. As a % of those who signed in.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalRegistered, row.percentageRegistered),
  },
  {
    label: "Checked in",
    name: "checkedInCompanion",
    tooltip:
      "Set their own pitch in the portal, as a % of eligible arrivals. The rest were handled by SMS, by reception, or automatically at the barrier when the pitch was already assigned.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(
        row.totalCheckedInCompanion,
        row.percentageCheckedInCompanion,
      ),
  },
  {
    label: "Adoption",
    name: "adopted",
    tooltip:
      "Used the Companion for anything — checking in, confirming a registration, or both — as a % of everyone who could have used it. This is the same number as Companion Used on the Overview.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalCompanionUsed, row.percentageAdopted),
  },
];

const dataQualityColumns: ColumnDefinition<DataQualityReportRow>[] = [
  { label: "Site", name: "siteName", getValue: (row) => row.siteName },
  {
    label: "Arrivals",
    name: "arrivals",
    tooltip:
      "Bookings due to start in the period, including any that never turned up.",
    getValue: (row) => `${row.totalArrivals}`,
  },
  {
    label: "Any problem",
    name: "anyDefect",
    tooltip:
      "Distinct bookings with at least one problem. The columns to the right can overlap on one booking, so they add up to more than this.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalAnyDefect, row.percentageAnyDefect),
  },
  {
    label: "No registration",
    name: "noRegistration",
    tooltip:
      "No vehicle on the booking at all — the feed sent a placeholder like 'TBC', which is discarded on import. The camera can never match these.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalNoRegistration, row.percentageNoRegistration),
  },
  {
    label: "Invalid registration",
    name: "invalidRegistration",
    tooltip:
      "A plate is stored but it cannot be a real one — all letters, all digits, punctuation, or the wrong length. It will never match at the camera.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(
        row.totalInvalidRegistration,
        row.percentageInvalidRegistration,
      ),
  },
  {
    label: "No phone",
    name: "noPhone",
    tooltip:
      "No number to text, so no pitch SMS and no Companion link — the guest can only be checked in at reception.",
    renderHtml: true,
    getValue: (row) => withPercentage(row.totalNoPhone, row.percentageNoPhone),
  },
  {
    label: "Invalid phone",
    name: "invalidPhone",
    tooltip:
      "A number is stored but it isn't dialable — often a literal 'N/A' that passed through unchanged.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalInvalidPhone, row.percentageInvalidPhone),
  },
];
</script>

<style>
/* No styles needed */
</style>
